import type { Currency, FormulaWarningDto, Pillar, Unit } from 'chery-contracts';
import {
  astToTokens,
  dimensionLabel,
  formatFormula,
  inferFormulaUnit,
  parseFormula,
  PILLARS,
  tokensToAst,
  type FormulaNode,
  type FormulaToken,
} from 'chery-kpi-engine';
import type { KpiRow } from './kpi-display';

/**
 * Presentation rules of the formula builder (plan §3.1, §7) — pure, so the token labels, the live preview, the
 * structural errors and the unit inference shown while building are unit-tested. The server re-validates every
 * rule; this only spares the author a round trip.
 */

export type FormulaOperand = Pick<KpiRow, 'id' | 'code' | 'name' | 'pillar' | 'unit' | 'currency' | 'status'>;

/** KPIs a formula may use: any visible, non-Text KPI except the one being edited (inactive ones are offered, marked). */
export const formulaOperands = (rows: readonly KpiRow[], selfId: string | null): FormulaOperand[] =>
  rows.filter((row) => row.id !== selfId && row.unit !== 'TEXT' && !row.restricted);

/** Operands grouped by pillar in the fixed pillar order, for the `<optgroup>`s of the KPI picker. */
export const operandsByPillar = (operands: readonly FormulaOperand[]): { pillar: Pillar; operands: FormulaOperand[] }[] =>
  PILLARS.map((pillar) => ({ pillar, operands: operands.filter((operand) => operand.pillar === pillar) })).filter(
    (group) => group.operands.length > 0,
  );

/** The builder's starting tokens for a stored formula; none for a manual KPI or a formula that cannot be read. */
export const tokensFromFormula = (formula: unknown): FormulaToken[] => {
  if (formula === null || formula === undefined) {
    return [];
  }

  const parsed = parseFormula(formula);

  return parsed.ok ? astToTokens(parsed.ast) : [];
};

/** A typed constant: plain decimal notation, thousands separators ignored; `null` when it is not a finite number. */
export const parseConstant = (raw: string): number | null => {
  const text = raw.trim().replace(/,/g, '');

  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(text)) {
    return null;
  }

  return Number(text);
};

export type FormulaStatus =
  | { kind: 'empty' }
  | { kind: 'invalid'; message: string }
  | {
      kind: 'ok';
      ast: FormulaNode;
      /** `Vehicles sold ÷ Leads generated × 100`. */
      preview: string;
      /** `a plain number`, `JOD`, `Days` — what the formula produces; `null` when the units do not work out. */
      produces: string | null;
      /** Why the units do not work out, with the engine's message. */
      unitError: string | null;
      /** Ambiguous shapes the server will ask the author to confirm. */
      warnings: string[];
    };

/** Everything the builder shows under the tokens, recomputed on every change. */
export const formulaStatus = (
  tokens: readonly FormulaToken[],
  declared: { unit: Unit; currency: Currency | null },
  operands: readonly FormulaOperand[],
): FormulaStatus => {
  if (tokens.length === 0) {
    return { kind: 'empty' };
  }

  const parsed = tokensToAst(tokens);

  if (!parsed.ok) {
    return { kind: 'invalid', message: parsed.error };
  }

  const byId = new Map(operands.map((operand) => [operand.id, operand]));
  const preview = formatFormula(parsed.ast, (id) => byId.get(id)?.name ?? 'Unknown KPI');
  const inference = inferFormulaUnit(parsed.ast, declared, (id) => byId.get(id) ?? null);
  const inactive = [...new Set(tokens.flatMap((token) => (token.kind === 'kpi' ? [token.kpiId] : [])))]
    .map((id) => byId.get(id))
    .filter((operand): operand is FormulaOperand => operand?.status === 'INACTIVE')
    .map((operand) => `"${operand.name}" is inactive — this KPI will show blank values until it is reactivated.`);

  return {
    kind: 'ok',
    ast: parsed.ast,
    preview,
    produces: inference.ok ? dimensionLabel(inference.produces) : null,
    unitError: inference.ok ? null : inference.error,
    warnings: [...inference.warnings.map((warning) => warning.message), ...inactive],
  };
};

/** The structured warnings of a 409 from `kpis.create` / `kpis.update`, or none when the body is anything else. */
export const formulaWarningsFrom = (data: unknown): FormulaWarningDto[] => {
  if (typeof data !== 'object' || data === null || (data as { needsConfirmation?: unknown }).needsConfirmation !== true) {
    return [];
  }

  const { warnings } = data as { warnings?: unknown };

  return Array.isArray(warnings)
    ? warnings.filter(
        (warning): warning is FormulaWarningDto =>
          typeof warning === 'object' && warning !== null && typeof (warning as FormulaWarningDto).message === 'string',
      )
    : [];
};
